import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import {
  Box,
  IconButton,
  Paper,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import _ from 'lodash';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import * as Yup from 'yup';
import { ApiResultModal } from '../../../components/modal/ApiResultHandler';
import { ApproveModal } from '../../../components/modal/ApproveModal';
import { ModalText, useModalToggle } from '../../../components/modal/Modal';
import {
  BulkUploadButton,
  PrimaryButton,
  PrimaryOutlinedButton,
} from '../../../components/style/ButtonStyle';
import { TableBackground } from '../../../components/style/CommonStyle';
import {
  PrettyScrollbarTableWrap,
  PrimaryBorderTable,
} from '../../../components/style/TableStyle';
import {
  CountingText,
  DetailInfoText,
  FormTitle,
} from '../../../components/style/TextStyle';
import { useAuthAxios, useAuthDownload } from '../../../hooks/useAuthAxios';
import {
  FormErrorModal,
  makeYupValidator,
  useYupValidationResolver,
} from '../../../hooks/useYupValidationResolver';
import {
  DownloadIcon,
  EditIcon,
  UploadIcon,
} from '../../../layouts/dashboard/nav/IconButton';
import { responsiveMargin } from '../../../utils/enum';
import { fDate } from '../../../utils/formatTime';
import { FACILITIES_SCHEMA } from './FacilityBulkSchema';
import { facilityHeader } from './FacilityListHeader';

export default function CreateBulkFacilityPage() {
  const navigate = useNavigate();

  const resolver = useYupValidationResolver(
    Yup.object().shape({
      facilities: FACILITIES_SCHEMA,
    }),
  );

  const form = useForm<any>({
    mode: 'onTouched',
    resolver,
    defaultValues: {
      facilities: [],
    },
    shouldUnregister: false,
  });
  const facilities = form.watch('facilities');
  const isEmpty = _.isEmpty(facilities);

  return (
    <Stack mx={responsiveMargin} spacing={5} mt={10} mb={5}>
      <IconButton
        sx={{ width: 'fit-content', justifyContent: 'flex-start' }}
        onClick={() => navigate(`/facility`)}
      >
        <KeyboardArrowLeftIcon />
      </IconButton>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <FormTitle>시설물 일괄 등록</FormTitle>
        <Stack direction="row" spacing={1}>
          {isEmpty ? (
            <DownloadTempleteButton />
          ) : (
            <InitFormButton form={form} />
          )}
          <PostfacilitiesButton form={form} isEmpty={isEmpty} />
        </Stack>
      </Stack>
      {isEmpty ? (
        <TableBackground
          spacing={3}
          alignItems="center"
          justifyContent="center"
          sx={{ py: 10 }}
        >
          <ExcelUploadButton form={form} />
          <DetailInfoText>선택된 파일 없음</DetailInfoText>
          <DetailInfoText>
            파일 선택 후 데이터를 업로드 할 수 있습니다.
            <br />
            제공된 양식을 다운로드 받은 후 이용하세요.
          </DetailInfoText>
        </TableBackground>
      ) : (
        <>
          <Stack
            direction="row"
            sx={{ w: '100%' }}
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack>
              <CountingText>총 {facilities?.length}건</CountingText>
            </Stack>
            <Box />
          </Stack>
          <PrettyScrollbarTableWrap>
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: '10px',
              }}
            >
              <PrimaryBorderTable sx={{ minWidth: 3130 }} size="small">
                <TableHead>
                  <TableRow>
                    {_.map(facilityHeader, t => (
                      <TableCell align="center" key={t.id}>
                        {t?.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {_.map(facilities, (facility, index) => {
                    return (
                      <TableRow key={index}>
                        <TableCell align="center">
                          {facility?.serialNumber || '-'}
                        </TableCell>
                        <TableCell align="center">
                          {facility?.title || '-'}
                        </TableCell>
                        <TableCell align="center">
                          {facility?.categoryTitle || '-'}
                        </TableCell>
                        <TableCell align="center">
                          {facility?.subCategoryTitle || '-'}
                        </TableCell>
                        <TableCell align="center">
                          {facility?.addressName || '-'}
                        </TableCell>
                        <TableCell align="center">
                          {facility?.addressNote || '-'}
                        </TableCell>
                        <TableCell align="center">
                          {fDate(facility?.dateOfInstall, 'yyyy-MM-dd') || '-'}
                        </TableCell>
                        <TableCell align="center">
                          {facility?.ownershipType || '-'}
                        </TableCell>
                        <TableCell align="center">
                          {facility?.usedAs || '-'}
                        </TableCell>
                        <TableCell align="center">
                          {facility?.occupyingSpace || '-'}
                        </TableCell>
                        <TableCell align="center">
                          {facility?.installCostNation || '-'}/
                          {facility?.installCostCity || '-'}/
                          {facility?.installCostDistrict || '-'}
                        </TableCell>
                        <TableCell align="center">
                          {facility?.installedDepartment || '-'}
                        </TableCell>
                        <TableCell align="center">
                          {facility?.maintenanceDepartment || '-'}
                        </TableCell>
                        <TableCell align="center">
                          {facility?.managingDepartment || '-'}
                        </TableCell>
                        <TableCell align="center">
                          {facility?.contactNumber || '-'}
                        </TableCell>
                        <TableCell align="center">
                          {facility?.consignedOrg || '-'}
                        </TableCell>
                        <TableCell align="center">
                          {fDate(facility?.consignedStartDate, 'yyyy-MM-dd') ||
                            '-'}
                          ~
                          {fDate(facility?.consignedEndDate, 'yyyy-MM-dd') ||
                            '-'}
                        </TableCell>
                        <TableCell align="center">
                          {facility?.notes || '-'}
                        </TableCell>
                        <TableCell align="center">
                          {facility?.inspectionScore || '-'}
                        </TableCell>
                        <TableCell align="center">
                          {fDate(facility?.inspectionDate, 'yyyy-MM-dd') || '-'}
                        </TableCell>
                        <TableCell align="center">
                          {facility?.inspectionResultTypes || '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </PrimaryBorderTable>
            </TableContainer>
          </PrettyScrollbarTableWrap>
        </>
      )}
    </Stack>
  );
}

const validatefacilities = makeYupValidator(FACILITIES_SCHEMA);
function ExcelUploadButton({ form }) {
  const fileInpRef = useRef<any>();
  const [mockForm, setMockForm] = useState<any>({ formState: { errors: {} } });
  async function onSelect(e) {
    e.preventDefault();

    const reader = new FileReader();
    reader.readAsArrayBuffer(e.target.files[0]);
    reader.onload = async function (e) {
      const data = new Uint8Array((reader as any).result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];

      const colLabelToFacilityField = {
        A: 'serialNumber',
        B: 'title',
        C: 'categoryTitle',
        D: 'subCategoryTitle',
        E: 'addressName',
        F: 'addressNote',
        G: 'dateOfInstall',
        H: 'ownershipType',
        I: 'usedAs',
        J: 'occupyingSpace',
        K: 'installCostNation',
        L: 'installCostCity',
        M: 'installCostDistrict',
        N: 'installedDepartment',
        O: 'maintenanceDepartment',
        P: 'managingDepartment',
        Q: 'contactNumber',
        R: 'consignedOrg',
        S: 'consignedStartDate',
        T: 'consignedEndDate',
        U: 'notes',
        V: 'inspectionScore',
        W: 'inspectionDate',
        X: 'inspectionResultTypes',
      };
      let facilities = [];

      _.keys(sheet).forEach(cellKey => {
        const colLabel = cellKey.slice(0, 1);
        const rowNum = +cellKey.slice(1);

        if (rowNum === 1) {
          return;
        }

        const fieldName = colLabelToFacilityField[colLabel];
        const value = sheet[cellKey].v || '';
        const rowFieldKey = `${rowNum}.${fieldName}`;
        if (fieldName === 'inspectionResultTypes') {
          const inspectionResultArray = _.compact(_.split(_.trim(value), ','));
          _.set(facilities, rowFieldKey, inspectionResultArray);
        } else if (fieldName === 'contactNumber') {
          _.set(facilities, rowFieldKey, parseInt(value, 10));
        } else if (
          fieldName === 'inspectionDate' ||
          fieldName === 'dateOfInstall' ||
          fieldName === 'consignedStartDate' ||
          fieldName === 'consignedEndDate' ||
          fieldName === 'consignedDate'
        ) {
          const dateValue = XLSX.SSF.parse_date_code(value);
          const date = new Date(
            Date.UTC(dateValue?.y, dateValue?.m - 1, dateValue?.d),
          );

          // Get the date part without the time
          const formattedDate = date.toISOString().split('T')[0];

          _.set(facilities, rowFieldKey, formattedDate);
        } else {
          _.set(facilities, rowFieldKey, value);
        }
      });
      facilities = facilities.filter(_.identity);
      const validation = await validatefacilities(facilities);

      if (_.isEmpty(validation.errors)) {
        form.setValue('facilities', facilities);
      } else {
        setMockForm({
          formState: validation,
          clearErrors: () => setMockForm({ formState: { errors: {} } }),
        });
      }
      fileInpRef.current.value = null;
    };
  }

  return (
    <>
      <BulkUploadButton
        startIcon={<UploadIcon />}
        onClick={e => {
          fileInpRef.current.click();
        }}
      >
        엑셀 파일 선택
      </BulkUploadButton>

      <FormErrorModal form={mockForm} title="엑셀 업로드" />

      <input
        style={{ display: 'none' }}
        type="file"
        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
        ref={fileInpRef}
        onChange={onSelect}
        multiple={false}
      />
    </>
  );
}

function PostfacilitiesButton({ form, isEmpty }: any) {
  const [postfacilitiesResult, postfacilities] = useAuthAxios(
    {
      method: 'POST',
      url: '/api/v1/facility/bulk',
    },
    { manual: true },
  );

  const onSubmit = form.handleSubmit(({ facilities }) => {
    const payload = {
      facilities: facilities.map(f => ({
        latitude: f.latitude,
        longitude: f.longitude,
        addressName: f.addressName,
        addressNote: f.addressNote,
        categoryId: f.categoryId,
        categoryTitle: f.categoryTitle,
        categoryType: f.categoryType,
        title: f.title,
        groupSerial: _.join(_.split(f.serialNumber, '-'), '')?.slice(
          0,
          _.join(_.split(f.serialNumber, '-'), '')?.length - 2,
        ),
        serialWithGroup: _.join(_.split(f.serialNumber, '-'), '')?.slice(
          _.join(_.split(f.serialNumber, '-'), '')?.length - 2,
        ),
        dateOfInstall: fDate(f.dateOfInstall, 'yyyy-MM-dd'),
        maintenanceDepartment: f.maintenanceDepartment,
        subCategoryId: f.subCategoryId,
        subCategoryTitle: f.subCategoryTitle,
        ownershipType: f.ownershipType,
        usedAs: f.usedAs,
        occupyingSpace: f.occupyingSpace,
        installCostNation: f.installCostNation,
        installCostCity: f.installCostCity,
        installCostDistrict: f.installCostDistrict,
        installDepartment: f.installDepartment,
        manageDepartment: f.manageDepartment,
        contactNumber: f.contactNumber,
        consignedOrg: f.consignedOrg,
        consignedStartDate:
          fDate(f.consignedStartDate, 'yyyy-MM-dd') === ''
            ? null
            : fDate(f.consignedStartDate, 'yyyy-MM-dd'),
        consignedEndDate:
          fDate(f.consignedEndDate, 'yyyy-MM-dd') === ''
            ? null
            : fDate(f.consignedEndDate, 'yyyy-MM-dd'),
        administrativeRegion: f.administrativeRegion,
        actualRegion: f.actualRegion,
        notes: f.notes,
        facilityId: f.facilityId,
        inspectionScore: f.inspectionScore,
        inspectionDate:
          fDate(f.inspectionDate, 'yyyy-MM-dd') === ''
            ? undefined
            : fDate(f.inspectionDate, 'yyyy-MM-dd'),
        inspectionResult: f.inspectionResult,
        inspectionNote: f.inspectionNote,
      })),
    };

    postfacilities({ data: payload });
  });

  const navigate = useNavigate();
  return (
    <>
      <PrimaryButton
        startIcon={<EditIcon />}
        onClick={onSubmit}
        sx={{ minWidth: 140 }}
        disabled={isEmpty ? true : false}
      >
        일괄 등록
      </PrimaryButton>
      <ApiResultModal
        apiResult={postfacilitiesResult}
        successTitle="등록됐습니다."
        successCode={201}
        successContent="일괄등록이 완료되었습니다."
        onSuccessClose={() => {
          navigate('/facility');
        }}
        failTitle="등록 실패"
        failContent="입력값을 확인해주세요."
        ErrorCodeTable={{
          PAYLOAD_INVALID: { title: '입력값을 확인해주세요' },
          DUPLICATE_SERIAL_NUMBER: {
            title: '관리번호 중복',
            content: '이미 존재하는 관리번호입니다.',
          },
          SERIAL_NUMBER_DOES_NOT_MATCH: {
            title: '시설명 확인',
            content: `해당 관리번호로 등록된 시설명이 존재합니다.\n 등록된 시설명을 확인해 주세요.`,
          },
        }}
      />
    </>
  );
}

function InitFormButton({ form }: any) {
  const { on, open, close } = useModalToggle();

  return (
    <>
      <PrimaryOutlinedButton onClick={open}>초기화</PrimaryOutlinedButton>
      <ApproveModal
        on={on}
        onClose={close}
        title="초기화"
        onConfirm={() => {
          form.setValue('facilities', []);
          close();
        }}
        isPrimaryColor={false}
      >
        <Stack direction="row" justifyContent="center" sx={{ pt: 3, pb: 5 }}>
          <ModalText>초기화 하시겠습니까?</ModalText>
        </Stack>
      </ApproveModal>
    </>
  );
}

function DownloadTempleteButton() {
  const [download] = useAuthDownload('관리대장 양식', {
    method: 'POST',
    url: `/api/v1/facility/downloadTemplate`,
  });
  return (
    <PrimaryOutlinedButton
      sx={{ minWidth: 174 }}
      startIcon={<DownloadIcon />}
      onClick={() => {
        download();
      }}
    >
      양식 다운로드
    </PrimaryOutlinedButton>
  );
}
